import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from '../../hooks/use-toast';
import adminApi from '../../lib/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CollectionsManager = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCollection, setEditingCollection] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadCollections = useCallback(async () => {
    try {
      const response = await adminApi.get('/api/admin/collections');
      setCollections(response.data);
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les collections", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCollections(); }, [loadCollections]);

  const handleDelete = async (collectionId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette collection ?')) return;
    try {
      await adminApi.delete(`/api/admin/collections/${collectionId}`);
      toast({ title: "Collection supprimée avec succès" });
      loadCollections();
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de supprimer la collection", variant: "destructive" });
    }
  };

  const handleSave = async (collectionData) => {
    try {
      if (collectionData.id && editingCollection) {
        await adminApi.put(`/api/admin/collections/${collectionData.id}`, collectionData);
        toast({ title: "Collection mise à jour avec succès !" });
      } else {
        await adminApi.post('/api/admin/collections', collectionData);
        toast({ title: "Collection créée avec succès !" });
      }

      setEditingCollection(null);
      setShowForm(false);
      loadCollections();
    } catch (error) {
      toast({
        title: "Erreur",
        description: error.response?.data?.detail || "Impossible de sauvegarder",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400 text-lg">Chargement des collections...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Gestion des Collections</h1>
          <p className="text-gray-400">Organisez vos produits par catégories</p>
        </div>
        <Button
          onClick={() => {
            setEditingCollection(null);
            setShowForm(true);
          }}
          className="bg-orange-600 hover:bg-orange-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle Collection
        </Button>
      </div>

      {showForm && (
        <CollectionForm
          collection={editingCollection}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingCollection(null);
          }}
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {collections.map((collection) => (
          <div
            key={collection.id}
            className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-gray-700 transition-all hover:shadow-xl"
          >
            <div className="relative h-48 overflow-hidden">
              {collection.image && (
                <img
                  src={collection.image}
                  alt={collection.name}
                  className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
            </div>
            <div className="p-6">
              <h3 className="text-white font-semibold text-xl mb-2">{collection.name}</h3>
              <p className="text-gray-400 text-sm mb-4 line-clamp-2">{collection.description}</p>
              
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingCollection(collection);
                    setShowForm(true);
                  }}
                  className="flex-1 border-gray-700 text-white hover:bg-gray-800"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(collection.id)}
                  className="border-red-700 text-red-500 hover:bg-red-900"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {collections.length === 0 && (
        <div className="text-center py-20 bg-gray-900 rounded-lg border border-gray-800">
          <p className="text-gray-400 text-lg mb-4">Aucune collection pour le moment</p>
          <Button onClick={() => setShowForm(true)} className="bg-orange-600 hover:bg-orange-700">
            Créer votre première collection
          </Button>
        </div>
      )}
    </div>
  );
};

const CollectionForm = ({ collection, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: collection?.name || '',
    slug: collection?.slug || '',
    image: collection?.image || '',
    description: collection?.description || '',
    id: collection?.id || undefined
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.slug || !formData.image) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires",
        variant: "destructive"
      });
      return;
    }

    onSave(formData);
  };

  const generateSlug = () => {
    const slug = formData.name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    setFormData({ ...formData, slug });
  };

  return (
    <div className="bg-gray-900 rounded-lg p-8 border border-gray-800 mb-8">
      <h2 className="text-2xl font-bold text-white mb-6">
        {collection ? '✏️ Modifier la collection' : '➕ Nouvelle collection'}
      </h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Nom de la collection *
            </label>
            <Input
              placeholder="Kits LED de Fête"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="bg-gray-800 text-white border-gray-700"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Slug (URL) *
            </label>
            <div className="flex gap-2">
              <Input
                placeholder="kits-led-fete"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                required
                className="bg-gray-800 text-white border-gray-700"
              />
              <Button type="button" onClick={generateSlug} variant="outline" className="border-gray-700 text-white">
                Auto
              </Button>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Image (URL) *
          </label>
          <Input
            placeholder="https://exemple.com/image-collection.jpg"
            value={formData.image}
            onChange={(e) => setFormData({ ...formData, image: e.target.value })}
            required
            className="bg-gray-800 text-white border-gray-700"
          />
          {formData.image && (
            <div className="mt-3">
              <img src={formData.image} alt="Aperçu" className="w-full h-48 object-cover rounded-lg" onError={(e) => e.target.style.display = 'none'} />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Description *
          </label>
          <Textarea
            placeholder="Description de la collection..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={4}
            className="bg-gray-800 text-white border-gray-700"
          />
        </div>

        <div className="flex gap-4 pt-4">
          <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700 py-3">
            <Save className="w-4 h-4 mr-2" />
            {collection ? 'Mettre à jour' : 'Créer la collection'}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel} className="border-gray-700 text-white hover:bg-gray-800">
            <X className="w-4 h-4 mr-2" />
            Annuler
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CollectionsManager;
